import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShoppingCart, DollarSign, Tractor } from "lucide-react";
import { useScrollAnimation, fadeInUp, staggerContainer, AnimatedDiv, AnimatedH2 } from "@/hooks/useScrollAnimation";
import farmBuy from "@/assets/farm-almond-blossoms.jpg";
import farmSell from "@/assets/farm-land-for-sale.jpg";
import farmManage from "@/assets/farm-pistachio-orchard.jpg";

const Services = () => {
  const { ref, controls } = useScrollAnimation();

  const services = [
    {
      icon: ShoppingCart,
      title: "Buy A Farm",
      description: "Whether you're a first-time investor or expanding your portfolio, we help you find the perfect farmland. From almond orchards to pistachio groves, we identify prime agricultural properties with strong ROI potential.",
      image: farmBuy,
      link: "/buy-a-farm",
      cta: "Explore buying a farm",
      alt: "Almond blossoms in California Central Valley farm for sale",
    },
    {
      icon: DollarSign,
      title: "Sell A Farm",
      description: "As farmers ourselves, we understand the true value of your land. Our deep market knowledge and extensive buyer network ensure you get the best price for your agricultural property.",
      image: farmSell,
      link: "/sell-a-farm",
      cta: "See how we sell farms",
      alt: "California farmland for sale with golden wheat fields",
    },
    {
      icon: Tractor,
      title: "Farm Management",
      description: "From crop planning to irrigation management, we provide comprehensive farm management services. We specialize in almonds and pistachios, helping you maximize yield and profitability.",
      image: farmManage,
      link: "/farm-management",
      cta: "View farm management services",
      alt: "Pistachio orchard farm management in Central Valley California",
    },
  ];

  return (
    <section className="py-20 md:py-28 bg-muted">
      <div className="container mx-auto px-4 md:px-6">
        <AnimatedDiv
          ref={ref}
          initial="hidden"
          animate={controls}
          variants={fadeInUp}
          className="text-center mb-16"
        >
          <p className="text-sm font-medium text-primary tracking-[0.3em] uppercase mb-4">What We Do</p>
          <AnimatedH2 variants={fadeInUp} className="text-3xl md:text-5xl heading-luxury text-foreground mb-4">
            Our Services
          </AnimatedH2>
          <div className="section-divider mt-4"></div>
        </AnimatedDiv>

        <AnimatedDiv
          initial="hidden"
          animate={controls}
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto"
        >
          {services.map((service, index) => (
            <AnimatedDiv key={index} variants={fadeInUp} className="h-full">
              <Card className="overflow-hidden bg-card border-0 shadow-lg hover:shadow-xl transition-all duration-500 h-full group flex flex-col">
                <div className="overflow-hidden h-56">
                  <img
                    src={service.image}
                    alt={service.alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                    width={1920}
                    height={1080}
                  />
                </div>
                <CardContent className="p-8 flex flex-col flex-grow">
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-accent rounded-full mb-5 -mt-12 relative z-10 shadow-lg border-4 border-card">
                    <service.icon className="w-6 h-6 text-accent-foreground" />
                  </div>
                  
                  <h3 className="text-2xl font-semibold text-foreground mb-3">
                    {service.title}
                  </h3>
                  
                  <p className="text-muted-foreground leading-relaxed mb-6 flex-grow">
                    {service.description}
                  </p>
                  
                  <a href={service.link} aria-label={`${service.cta} — ${service.title}`}>
                    <Button className="w-full btn-luxury">
                      {service.cta}
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </AnimatedDiv>
          ))}
        </AnimatedDiv>
      </div>
    </section>
  );
};

export default Services;
